/**
 * @module app.um
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthService } from 'app/um/services/auth-service';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { FlagOptions } from 'app/um/components/auth-statuses/statuses';
import { deepClone } from 'app/shared/lib/utilities';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';

enum DeterminationOptions {
    None = 'None',
    Approved = 'Approved',
    Denied = 'Denied',
    Modified = 'Modified'
}

enum ExclusiveEventOptions {
    initialStatus = 'Initial Status',
    dataEntryCompleted = 'Data Entry Completed'
}

interface IAuthStatusResults { isSuccessful?: boolean; toasterDisplayed?: boolean };

@autoinject()
export class SaveStatus extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView, changeHandler: 'reset' })
    public authStatus: UM.AuthStatus = { id: null };

    // Drop Down Options
    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected statuses: UM.AuthStatus[] = [];

    @bindable.boolean
    protected syncToClaimsSystemEnabled: boolean = false;

    @observable
    private addDetermination: boolean = false;

    private authStatusId: string | null = null;
    // Value Bindings
    protected status: string = '';
    protected code: string = '';
    protected determination: string | null = null;
    protected flags: Array<string> = [];
    protected transitions: UM.AuthStatus[] = [];
    protected exclusiveEvent: string | null = null;
    protected providerChangesRequestedEvent: boolean = false;
    protected id: guid = guid();

    // Drop Down Options
    private determinationOptions: string[] = [];
    private flagOptions: Array<string> = [];
    // these flags should only appear in the System Flags dropdown if Add Determination is checked
    private determinationFlags: string[] = [];
    private eventOptions: string[] = [];

    protected editsAllowedInProviderPortal: boolean = false;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.determinationOptions = <string[]>Object.values(DeterminationOptions);
        this.eventOptions = <string[]>Object.values(ExclusiveEventOptions);
        this.flagOptions = [
            FlagOptions.allowEditsInProviderPortal,
            FlagOptions.allowNullDateOfService,
            FlagOptions.allowStatusDateEdit,
            FlagOptions.isApproval,
            FlagOptions.isOpen,
            FlagOptions.isTurnAroundTimeBase,
            FlagOptions.isVoided,
        ];
        this.reset();
    }

    ////****************** Events ***************************************************////
    protected syncToClaimsSystemEnabledChanged(): void {
        // if Sync to Claims System configured for tenant, add Send to Claims System as possible System Flag option
        if(this.syncToClaimsSystemEnabled) this.determinationFlags.push(FlagOptions.sendToClaimsSystem);
    }

    protected addDeterminationChanged(): void {
        if(!this.addDetermination && this.determination != DeterminationOptions.None) {
            this.determination = DeterminationOptions.None;
        }
        this.setDeterminationFlags();
    }

    protected flagsChanged(): void {
        this.editsAllowedInProviderPortal = this.flags.includes(FlagOptions.allowEditsInProviderPortal);
    }

    ////****************** Functions ************************************************////
    // Set up certain flags based on Determination selection
    protected setDeterminationFlags(): void {
        // Certain  flags can only be selected if Add Determination is true
        if(this.flagOptions?.length) {
            if(this.addDetermination) {
                this.flagOptions.push(...this.determinationFlags);
            } else {
                this.flagOptions = this.flagOptions.filter(option => !this.determinationFlags.includes(option));
            }
        }

        // If add determination is unchecked, filter out determination flags from currently selected
        if(!this.addDetermination && this.flags?.length) {
            this.flags = this.flags.filter(flag => !this.determinationFlags.includes(flag));
        }
    }

    // Build Auth Status to send over to the api
    protected buildAuthStatus(): UM.AuthStatus {
        let allowEditsInProviderPortal: boolean = false, allowStatusDateEdit: boolean = false, allowNullDateOfService: boolean = false, isApproval: boolean = false,
            isOpen: boolean = false, isTurnAroundTimeBase: boolean = false, isVoided: boolean = false, sendToClaimsSystem: boolean = false;

        this.flags.forEach(flag => {
            switch(flag) {
                case FlagOptions.allowEditsInProviderPortal:
                    allowEditsInProviderPortal = true;
                    break;
                case FlagOptions.allowStatusDateEdit:
                    allowStatusDateEdit = true;
                    break;
                case FlagOptions.allowNullDateOfService:
                    allowNullDateOfService = true;
                    break;
                case FlagOptions.isApproval:
                    isApproval = true;
                    break;
                case FlagOptions.isOpen:
                    isOpen = true;
                    break;
                case FlagOptions.isTurnAroundTimeBase:
                    isTurnAroundTimeBase = true;
                    break;
                case FlagOptions.isVoided:
                    isVoided = true;
                    break;
                case FlagOptions.sendToClaimsSystem:
                    // Can only be set to true if add determination (aka isDetermination) is true
                    sendToClaimsSystem = this.addDetermination;
                    break;
            }
        });

        const returnTransitions = <Array<UM.AuthStatus>>this.transitions.map(t => getReference(t));

        const returnAuthStatus: UM.AuthStatus = {
            id: this.authStatus.id,
            description: this.status,
            code: this.code,
            flag: {
                // System Flags
                allowEditsInProviderPortal: allowEditsInProviderPortal,
                allowStatusDateEdit: allowStatusDateEdit,
                allowNullDateOfService: allowNullDateOfService,
                isApproval: isApproval,
                isOpen: isOpen,
                isTurnAroundTimeBase: isTurnAroundTimeBase,
                isVoided: isVoided,
                sendToClaimsSystem: sendToClaimsSystem,
            },
            transitions: returnTransitions
        };

        // Set auth status determination
        returnAuthStatus.determination = {
            isDetermination: this.addDetermination,
            result: this.determination ?? DeterminationOptions.None
        };

        return returnAuthStatus;
    }

    // Build Auth Status to send over to the api, exclusive to just status events
    protected buildAuthStatusEventsExclusive(): UM.AuthStatus {
        const returnAuthStatus: UM.AuthStatus = {
            id: this.authStatusId
        };

        switch(this.exclusiveEvent) {
            case ExclusiveEventOptions.initialStatus:
                returnAuthStatus.flag = {
                    isInitial: true
                };
                break;
            case ExclusiveEventOptions.dataEntryCompleted:
                returnAuthStatus.flag = {
                    dataEntryComplete: true
                };
                break;
        }

        return returnAuthStatus;
    }

    protected buildAuthStatusEventsProviderChangesRequested(): UM.AuthStatus {
        const returnAuthStatus: UM.AuthStatus = {
            id: this.authStatusId,
            flag: {
                providerChangesRequested: true
            }
        };

        return returnAuthStatus;
    }

    ////****************** IForm ****************************************************////
    public async reset(): Promise<void> {
        this.authStatusId = this.authStatus.id ?? null;
        this.status = this.authStatus.description || '';
        this.code = this.authStatus.code || '';
        this.flags = [];
        this.transitions = [];
        this.providerChangesRequestedEvent = false;
        this.exclusiveEvent = null;

        // Set Determination
        const determinationResult = this.authStatus.determination?.result;
        // Determination Found
        if(determinationResult && determinationResult.length > 1) {
            this.determination = determinationResult[0].toUpperCase() + determinationResult.substring(1);

            // If Determination is not under any of the determination options, default the value to "None".
            if(this.determinationOptions.indexOf(this.determination) < 0) {
                this.determination = DeterminationOptions.None;
            }

            this.addDetermination = this.authStatus.determination?.isDetermination ?? false;
        }
        // No Determination Found
        else {
            // Set determination to none in case addDetermination is already false.
            if(!this.addDetermination && this.determination != DeterminationOptions.None) {
                this.determination = DeterminationOptions.None;
            }
            // Default determination to none and determination is not shown
            else {
                this.addDetermination = false;
            }
        }

        const flags = this.authStatus.flag;
        if(flags) {
            // Set Flags
            this.flagOptions.forEach(option => {
                if(option === FlagOptions.allowEditsInProviderPortal && flags.allowEditsInProviderPortal) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.allowStatusDateEdit && flags.allowStatusDateEdit) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.allowNullDateOfService && flags.allowNullDateOfService) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.isApproval && flags.isApproval) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.isOpen && flags.isOpen) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.isTurnAroundTimeBase && flags.isTurnAroundTimeBase) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.isVoided && flags.isVoided) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.sendToClaimsSystem && flags.sendToClaimsSystem) {
                    this.flags.push(option);
                }
            });

            // Set Events
            if(flags.isInitial) {
                this.exclusiveEvent = ExclusiveEventOptions.initialStatus;
            } else if(flags.dataEntryComplete) {
                this.exclusiveEvent = ExclusiveEventOptions.dataEntryCompleted;
            }

            if(flags.providerChangesRequested) {
                this.providerChangesRequestedEvent = true;
            }
        }

        this.editsAllowedInProviderPortal = this.flags.includes(FlagOptions.allowEditsInProviderPortal);

        this.transitions = this.authStatus.transitions ? deepClone(this.authStatus.transitions) : [];
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');

    }

    public async submit(): Promise<boolean> {
        let authStatusResults: IAuthStatusResults = { isSuccessful: false, toasterDisplayed: false };

        try {
            this.state.beginSave();

            // Save Auth Status
            authStatusResults = await this.saveAuthStatus();

            // If an event was selected, push an exclusive update
            if(!!authStatusResults.isSuccessful && (this.exclusiveEvent || this.providerChangesRequestedEvent)) {
                authStatusResults = await this.updateAuthStatusEvents();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            if(!authStatusResults.toasterDisplayed) {
                this.toaster.standard.saved('um:auth.status.$long', authStatusResults.isSuccessful);
            }

            this.state.endSave();
            return authStatusResults.isSuccessful ?? false;
        }
    }

    ////****************** Service Calls ********************************************////

    // To Add/Update an AuthStatus
    @apiCall
    protected async saveAuthStatus(): Promise<IAuthStatusResults> {
        let isSuccessful = false;
        let toasterDisplayed = false;
        try {
            const response = await this.service.saveStatus(this.buildAuthStatus());
            if(!this.authStatusId) {
                this.authStatusId = response.id ?? null;
            }
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            // If Duplicate Status Code constraint violated, show toast
            if(error.status.errors) {
                const invalidCode = error.status.errors.find((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');

                if(invalidCode) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:auth.status.code.$long'), item: this.i18n.tr('um:auth.status.$long') })).show();
                } else {
                    this.toaster.standard.saved('um:auth.status.$long', false);
                }
            } else {
                this.toaster.standard.saved('um:auth.status.$long', false);
            }
            toasterDisplayed = true;
        }
        finally {
            return { isSuccessful: isSuccessful, toasterDisplayed: toasterDisplayed };
        }
    }

    // To Update an AuthStatus's Event information
    @apiCall
    protected async updateAuthStatusEvents(): Promise<IAuthStatusResults> {
        let isSuccessful = false;
        let toasterDisplayed = false;
        if(this.authStatusId) {
            try {
                const promises = [];
                if(this.exclusiveEvent) {
                    promises.push(this.service.updateStatusExclusive(this.buildAuthStatusEventsExclusive()));
                }
                if(this.providerChangesRequestedEvent) {
                    promises.push(this.service.updateStatusExclusive(this.buildAuthStatusEventsProviderChangesRequested()));
                }
                const responses = await Promise.all(promises);
                isSuccessful = responses.every((response: any) => response.isSuccessful);
            }
            catch(error) {
                // If Status already has Event flag, show toast
                if(error.status.errors) {
                    const eventAlreadyFlagged = error.status.errors.find((err: any) => (err.description as string).startsWith('Auth Status is already flagged'));

                    if(eventAlreadyFlagged) {
                        this.toaster.error().message(this.i18n.tr('um:local.authStatus.authStatusEventChangeError')).show();
                    } else {
                        this.toaster.standard.saved('um:auth.status.$long', false);
                    }
                } else {
                    this.toaster.standard.saved('um:auth.status.$long', false);
                }
                toasterDisplayed = true;
            }
            finally {
                return { isSuccessful: isSuccessful, toasterDisplayed: toasterDisplayed };
            }
        }
        else {
            return { isSuccessful: false, toasterDisplayed: false };
        }
    }
}
